import React, { useState, Fragment } from 'react';
import { Lead, Team } from '../types';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { ArrowLeft, Check, Send, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
interface DistributionReviewProps {
  leads: Lead[];
  teams: Team[];
  onBack: () => void;
  onReset: () => void;
}
export function DistributionReview({
  leads,
  teams,
  onBack,
  onReset
}: DistributionReviewProps) {
  const [isPushing, setIsPushing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Group leads by Team -> User
  const distributionSummary = teams.map(team => {
    const teamLeads = leads.filter(l => l.assignedTeamId === team.id);
    if (teamLeads.length === 0) return null;
    const userStats = team.users.map(user => {
      const count = teamLeads.filter(l => l.assignedUserId === user.id).length;
      return {
        user,
        count
      };
    }).filter(stat => stat.count > 0);
    return {
      team,
      totalLeads: teamLeads.length,
      userStats
    };
  }).filter(Boolean);
  const totalLeads = leads.filter(l => l.assignedTeamId && l.assignedUserId).length;
  const unassignedCount = leads.filter(l => !l.assignedTeamId || !l.assignedUserId).length;
  const handlePush = async () => {
    setIsPushing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsPushing(false);
    setIsSuccess(true);
    // Trigger confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: {
        y: 0.6
      },
      colors: ['#0D9488', '#F59E0B', '#3B82F6']
    });
  };
  if (isSuccess) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-2">
          <Check className="w-10 h-10 text-green-600" data-id="element-3" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-4">
          Successfully Pushed to Bonzo!
        </h2>
        <p className="text-lg text-slate-600 mb-8 max-w-md" data-id="element-5">
          {totalLeads} leads have been distributed to their respective campaigns
          and owners.
        </p>
        <Button variant="primary" size="lg" onClick={onReset} data-id="element-6">
          Process Another Batch
        </Button>
      </motion.div>;
  }
  return <div className="space-y-8" data-id="element-7">
      {/* Summary Header */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-8">
        <div data-id="element-9">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-10">
            Review Distribution
          </h2>
          <p className="text-slate-600 mt-1" data-id="element-11">
            Verify the assignments below before pushing to Bonzo.
          </p>
        </div>
        <div className="flex items-center gap-6" data-id="element-12">
          <div className="text-center" data-id="element-13">
            <div className="text-3xl font-bold text-[#0D9488]" data-id="element-14">
              {totalLeads}
            </div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-15">
              Leads to Push
            </div>
          </div>
          {unassignedCount > 0 && <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-md border border-amber-200" data-id="element-16">
              <AlertTriangle className="h-5 w-5" data-id="element-17" />
              <span className="font-medium" data-id="element-18">
                {unassignedCount} unassigned leads
              </span>
            </div>}
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-id="element-19">
        <table className="min-w-full divide-y divide-slate-200" data-id="element-20">
          <thead className="bg-slate-50" data-id="element-21">
            <tr data-id="element-22">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-23">
                Team / Campaign
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-24">
                Bonzo Owner
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-25">
                Lead Count
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200" data-id="element-26">
            {distributionSummary.map(item => <Fragment key={item?.team.id} data-id="element-27">
                {/* Team Header Row */}
                <tr className="bg-slate-50/50" data-id="element-28">
                  <td className="px-6 py-4 whitespace-nowrap" colSpan={3} data-id="element-29">
                    <div className="flex items-center gap-3" data-id="element-30">
                      <Badge color={item?.team.color} className="text-sm px-2 py-1" data-id="element-31">
                        {item?.team.name}
                      </Badge>
                      <span className="text-sm text-slate-500" data-id="element-32">
                        Campaign:{' '}
                        <span className="font-medium text-slate-900" data-id="element-33">
                          {item?.team.campaignName}
                        </span>
                      </span>
                      <span className="ml-auto text-sm font-bold text-slate-900 bg-slate-100 px-2 py-1 rounded" data-id="element-34">
                        Total: {item?.totalLeads}
                      </span>
                    </div>
                  </td>
                </tr>
                {/* User Rows */}
                {item?.userStats.map(stat => <tr key={stat.user.id} className="hover:bg-slate-50" data-id="element-35">
                    <td className="px-6 py-3 whitespace-nowrap pl-12" data-id="element-36">
                      <div className="w-px h-full bg-slate-200 absolute left-8 top-0" data-id="element-37" />
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap" data-id="element-38">
                      <div className="flex items-center gap-3" data-id="element-39">
                        <div className="h-6 w-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600" data-id="element-40">
                          {stat.user.avatarInitials}
                        </div>
                        <span className="text-sm text-slate-700" data-id="element-41">
                          {stat.user.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-right" data-id="element-42">
                      <span className="text-sm font-medium text-slate-900" data-id="element-43">
                        {stat.count}
                      </span>
                    </td>
                  </tr>)}
              </Fragment>)}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-slate-200" data-id="element-44">
        <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-46" />} onClick={onBack} disabled={isPushing} data-id="element-45">
          Back to Distribution
        </Button>
        <Button variant="primary" size="lg" className="w-full md:w-auto min-w-[200px]" rightIcon={<Send className="h-4 w-4" data-id="element-48" />} onClick={handlePush} isLoading={isPushing} disabled={unassignedCount > 0} data-id="element-47">
          {isPushing ? 'Pushing to Bonzo...' : 'Confirm & Push to Bonzo'}
        </Button>
      </div>
    </div>;
}